import { useState, useRef, useEffect } from "react";
import { createLead } from "../../lib/api";
import Reveal from "./Reveal";
import { toast } from "sonner";
import { ChevronDown, X } from "lucide-react";

const DEFAULT_SERVICES = ["Consultoria Imobiliária", "Consórcio", "Financiamento", "Design & Web Design"];
const DEFAULT_CONTACT_METHODS = ["WhatsApp", "Ligação Telefónica", "E-mail"];

const PREFIXES = [
  { code: "+351", country: "Portugal", flag: "🇵🇹" },
  { code: "+55", country: "Brasil", flag: "🇧🇷" },
  { code: "+1", country: "EUA / Canadá", flag: "🇺🇸" },
  { code: "+44", country: "Reino Unido", flag: "🇬🇧" },
  { code: "+34", country: "Espanha", flag: "🇪🇸" },
  { code: "+33", country: "França", flag: "🇫🇷" },
  { code: "+49", country: "Alemanha", flag: "🇩🇪" },
  { code: "+39", country: "Itália", flag: "🇮🇹" },
  { code: "+31", country: "Holanda", flag: "🇳🇱" },
  { code: "+41", country: "Suíça", flag: "🇨🇭" },
  { code: "+43", country: "Áustria", flag: "🇦🇹" },
  { code: "+32", country: "Bélgica", flag: "🇧🇪" },
  { code: "+48", country: "Polónia", flag: "🇵🇱" },
  { code: "+420", country: "Rep. Checa", flag: "🇨🇿" },
  { code: "+353", country: "Irlanda", flag: "🇮🇪" },
  { code: "+46", country: "Suécia", flag: "🇸🇪" },
  { code: "+47", country: "Noruega", flag: "🇳🇴" },
  { code: "+45", country: "Dinamarca", flag: "🇩🇰" },
  { code: "+358", country: "Finlândia", flag: "🇫🇮" },
  { code: "+81", country: "Japão", flag: "🇯🇵" },
  { code: "+86", country: "China", flag: "🇨🇳" },
  { code: "+91", country: "Índia", flag: "🇮🇳" },
  { code: "+61", country: "Austrália", flag: "🇦🇺" },
  { code: "+54", country: "Argentina", flag: "🇦🇷" },
  { code: "+52", country: "México", flag: "🇲🇽" },
  { code: "+56", country: "Chile", flag: "🇨🇱" },
  { code: "+57", country: "Colômbia", flag: "🇨🇴" },
  { code: "+51", country: "Peru", flag: "🇵🇪" },
  { code: "+58", country: "Venezuela", flag: "🇻🇪" },
  { code: "+244", country: "Angola", flag: "🇦🇴" },
  { code: "+238", country: "Cabo Verde", flag: "🇨🇻" },
  { code: "+245", country: "Guiné-Bissau", flag: "🇬🇼" },
  { code: "+239", country: "São Tomé", flag: "🇸🇹" },
  { code: "+258", country: "Moçambique", flag: "🇲🇿" },
  { code: "+230", country: "Maurícia", flag: "🇲🇺" },
  { code: "+971", country: "Emirados Árabes", flag: "🇦🇪" },
  { code: "+966", country: "Arábia Saudita", flag: "🇸🇦" },
  { code: "+972", country: "Israel", flag: "🇮🇱" },
  { code: "+90", country: "Turquia", flag: "🇹🇷" },
  { code: "+27", country: "África do Sul", flag: "🇿🇦" },
  { code: "+234", country: "Nigéria", flag: "🇳🇬" },
  { code: "+20", country: "Egito", flag: "🇪🇬" },
  { code: "+7", country: "Rússia", flag: "🇷🇺" },
  { code: "+82", country: "Coreia do Sul", flag: "🇰🇷" },
  { code: "+65", country: "Singapura", flag: "🇸🇬" },
  { code: "+60", country: "Malásia", flag: "🇲🇾" },
  { code: "+66", country: "Tailândia", flag: "🇹🇭" },
  { code: "+84", country: "Vietname", flag: "🇻🇳" },
  { code: "+63", country: "Filipinas", flag: "🇵🇭" },
  { code: "+62", country: "Indonésia", flag: "🇮🇩" },
];

function fuzzyMatch(query, item) {
  const q = query.toLowerCase().trim();
  if (!q) return 1;
  const score = [
    item.code.includes(q),
    item.country.toLowerCase().includes(q),
    item.code.replace("+", "").includes(q),
  ].filter(Boolean).length;
  return score;
}

function PrefixSelector({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  const selected = PREFIXES.find((p) => p.code === value) || PREFIXES[0];

  const filtered = PREFIXES
    .map((p) => ({ ...p, score: fuzzyMatch(search, p) }))
    .filter((p) => p.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 15);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => { setOpen(!open); setSearch(""); }}
        className="flex items-center gap-1.5 px-3 bg-transparent text-sm hover:border-champagne transition-colors w-full text-left"
        style={{ padding: "14px 12px 12px", fontSize: "15px", fontFamily: "inherit", borderBottom: "1px solid #8E857A" }}
      >
        <span className="text-base">{selected.flag}</span>
        <span className="text-softblack font-medium">{selected.code}</span>
        <ChevronDown size={14} className={`text-warmgray transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute bottom-full left-0 mb-2 w-64 bg-white border border-fendi rounded-lg shadow-xl z-50 overflow-hidden">
          <div className="p-2 border-b border-fendi/50">
            <div className="relative">
              <input
                autoFocus
                type="text"
                placeholder="Buscar por país ou prefixo..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full text-sm px-3 py-2 bg-fendi/10 rounded-md outline-none focus:bg-fendi/20 transition-colors"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-warmgray hover:text-softblack"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
          <div className="max-h-52 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-sm text-warmgray text-center">Nenhum resultado</p>
            ) : (
              filtered.map((p) => (
                <button
                  key={p.code + p.country}
                  type="button"
                  onClick={() => { onChange(p.code); setOpen(false); setSearch(""); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-fendi/20 transition-colors ${
                    p.code === value ? "bg-fendi/30" : ""
                  }`}
                >
                  <span className="text-lg">{p.flag}</span>
                  <span className="flex-1 text-left text-softblack">{p.country}</span>
                  <span className="text-warmgray font-mono text-xs">{p.code}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Contact({ sectionContent, formServices, contactMethods, globals, whatsappNumber, uiStrings, elementVisibility }) {
  const eyebrow = sectionContent?.eyebrow || "Contato";
  const headline = sectionContent?.headline || "Vamos conversar sobre o seu próximo passo.";
  const description = sectionContent?.description || "Conte-me sobre a sua intenção. Em até 24 horas você terá uma resposta — com estratégia, clareza e o cuidado que cada decisão merece.";
  const contactEmail = globals?.contact_email || "emyprates@gmail.com";
  const locations = globals?.locations || "Lisboa · Porto · Online";
  const whatsapp = whatsappNumber || globals?.whatsapp_number || "";
  
  const servicesList = formServices || DEFAULT_SERVICES;
  const methodsList = contactMethods || DEFAULT_CONTACT_METHODS;

  // UI Strings
  const formStrings = uiStrings?.form || {
    name_label: "Nome completo",
    name_placeholder: "Como podemos chamar você",
    email_label: "E-mail",
    email_placeholder: "seu@email.com",
    phone_label: "Telefone",
    phone_placeholder: "+351 ...",
    service_label: "Serviço",
    service_placeholder: "Selecione um serviço",
    message_label: "Mensagem",
    message_placeholder: "Conte um pouco sobre sua intenção…",
    submit_button: "Enviar mensagem",
    sending_button: "Enviando…"
  };

  const feedbackStrings = uiStrings?.feedback || {
    success_title: "Sua mensagem foi enviada com elegância.",
    success_message: "Em até 24 horas você terá retorno. Caso prefira, abrimos uma janela direta no WhatsApp.",
    error_message: "Não foi possível enviar agora. Tente novamente.",
    received_badge: "Recebido",
    send_new: "Enviar nova mensagem"
  };

  const detailStrings = uiStrings?.details || {
    whatsapp: "WhatsApp",
    email: "E-mail",
    atendimento: "Atendimento"
  };

  const [form, setForm] = useState({ 
    name: "", 
    email: "", 
    phonePrefix: "+351",
    phone: "", 
    service: "", 
    message: "",
    contactMethod: ""
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [emailError, setEmailError] = useState("");

  const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
    if (k === "email") setEmailError("");
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!EMAIL_REGEX.test(form.email)) {
      setEmailError("Por favor, insira um e-mail válido.");
      return;
    }
    setLoading(true);
    try {
      await createLead({ ...form, phone: `${form.phonePrefix} ${form.phone}` });
      setDone(true);
      toast.success(feedbackStrings.success_title);
    } catch (err) {
      console.error(err);
      toast.error(feedbackStrings.error_message);
    } finally {
      setLoading(false);
    }
  }

  const waDigits = whatsapp.replace(/\D/g, "");

  return (
    <section id="contato" data-testid="contact-section" className="bg-offwhite py-28 md:py-40">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-16">
        <Reveal className="lg:col-span-5">
          <p className="eyebrow text-warmgray">{eyebrow}</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-softblack mt-4 md:mt-6 leading-[1.05]">
            {headline}
          </h2>
          <p className="text-warmgray text-sm sm:text-base md:text-lg leading-relaxed mt-4 md:mt-8 font-light">
            {description}
          </p>
          {elementVisibility?.contact_info !== false && (
            <div className="mt-8 md:mt-12 space-y-3 md:space-y-4 text-sm">
              <Detail label={detailStrings.whatsapp || "WhatsApp"} value={whatsapp} href={`https://wa.me/${waDigits}`} />
              <Detail label={detailStrings.email || "E-mail"} value={contactEmail} href={`mailto:${contactEmail}`} />
              <Detail label={detailStrings.atendimento || "Atendimento"} value={locations} />
            </div>
          )}
        </Reveal>

        {elementVisibility?.contact_form !== false && (
          <Reveal delay={200} className="lg:col-span-7">
          {done ? (
            <div data-testid="contact-success" className="border border-fendi p-6 md:p-12 bg-white">
              <p className="eyebrow text-warmgray">{feedbackStrings.received_badge || "Recebido"}</p>
              <h3 className="font-serif text-2xl sm:text-3xl md:text-4xl text-softblack mt-3 md:mt-4">
                {feedbackStrings.success_title}
              </h3>
              <p className="text-warmgray mt-3 md:mt-4 text-sm md:text-base font-light">
                {feedbackStrings.success_message}
              </p>
              <button
                data-testid="contact-reset"
                className="btn-ghost mt-6 md:mt-8"
                onClick={() => { setDone(false); setForm({ name: "", email: "", phonePrefix: "+351", phone: "", service: "", message: "", contactMethod: "" }); setEmailError(""); }}
              >
                {feedbackStrings.send_new || "Enviar nova mensagem"}
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-5 md:space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <label className="block">
                  <span className="eyebrow text-warmgray">{formStrings.name_label}</span>
                  <input
                    required minLength={2}
                    data-testid="contact-input-name"
                    className="editorial-field mt-3"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder={formStrings.name_placeholder}
                  />
                </label>
                <label className="block">
                  <span className="eyebrow text-warmgray">{formStrings.email_label}</span>
                  <input
                    required type="email"
                    data-testid="contact-input-email"
                    className={`editorial-field mt-3 ${emailError ? "!border-b-destructive" : ""}`}
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder={formStrings.email_placeholder}
                  />
                  {emailError && <p className="text-destructive text-xs mt-2">{emailError}</p>}
                </label>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <label className="block">
                  <span className="eyebrow text-warmgray">{formStrings.phone_label || "Telefone"}</span>
                    <div className="flex gap-3 mt-3 items-end w-full">
                      <div className="w-[20%] flex-shrink-0">
                        <PrefixSelector value={form.phonePrefix} onChange={(v) => update("phonePrefix", v)} />
                      </div>
                      <input
                        required
                        data-testid="contact-input-phone"
                        className="editorial-field w-[80%]"
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                      />
                    </div>
                </label>
                <label className="block">
                  <span className="eyebrow text-warmgray">{formStrings.service_label}</span>
                  <select
                    data-testid="contact-input-service"
                    className="editorial-field mt-3 bg-transparent"
                    value={form.service}
                    onChange={(e) => update("service", e.target.value)}
                  >
                    <option value="" disabled>{formStrings.service_placeholder || "Selecione um serviço"}</option>
                    {servicesList.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <label className="block">
                  <span className="eyebrow text-warmgray">{formStrings.contact_method_label || "Prefere ser contactado via"}</span>
                  <select
                    data-testid="contact-input-method"
                    className="editorial-field mt-3 bg-transparent"
                    value={form.contactMethod}
                    onChange={(e) => update("contactMethod", e.target.value)}
                  >
                    <option value="" disabled>{formStrings.contact_method_placeholder || "Selecione um método"}</option>
                    {methodsList.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </label>
              </div>
              <label className="block">
                <span className="eyebrow text-warmgray">{formStrings.message_label}</span>
                <textarea
                  rows={4}
                  data-testid="contact-input-message"
                  className="editorial-field mt-3"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder={formStrings.message_placeholder}
                />
              </label>
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="btn-primary w-full sm:w-auto disabled:opacity-60"
                >
                  {loading ? formStrings.sending_button : formStrings.submit_button}
                </button>
              </div>
            </form>
          )}
        </Reveal>
      )}

      </div>
    </section>
  );
}

function Detail({ label, value, href }) {
  const content = (
    <div>
      <p className="eyebrow text-warmgray">{label}</p>
      <p className="text-softblack mt-2 link-underline">{value}</p>
    </div>
  );
  const isEmail = href?.startsWith("mailto:");
  return href ? (
    <a href={href} target={isEmail ? undefined : "_blank"} rel={isEmail ? undefined : "noopener noreferrer"} data-testid={`contact-detail-${label.toLowerCase()}`}>{content}</a>
  ) : (
    <div data-testid={`contact-detail-${label.toLowerCase()}`}>{content}</div>
  );
}
